// src/components/Footer.js
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import footerLogo from '../assets/footer-logo.png';
import footerBg from '../assets/footer-bg.png';

/* -------------------- Helper Components -------------------- */

const ContactInfoItem = ({ icon, title, text }) => {
    const linkClasses =
        "text-gray-400 text-sm hover:text-orange-500 transition-colors duration-300";

    const renderText = () => {
        if (title === "Phone") {
            return <a href={`tel:${text.replace(/\s/g, '')}`} className={linkClasses}>{text}</a>;
        }
        if (title === "Email") {
            return <a href={`mailto:${text}`} className={linkClasses}>{text}</a>;
        }
        return <p className="text-gray-400 text-sm">{text}</p>;
    };

    return (
        <div className="flex items-start mb-5">
            <div className="text-orange-500 text-2xl mr-4 mt-1">{icon}</div>
            <div>
                <h4 className="font-bold text-white text-lg">{title}</h4>
                {renderText()}
            </div>
        </div>
    );
};

const SocialLink = ({ href, icon }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="w-10 h-10 bg-gray-700 hover:bg-orange-500 rounded-full flex items-center justify-center text-white transition-colors duration-300"
    >
        {icon}
    </a>
);

/* -------------------- Footer Component -------------------- */

const Footer = () => {
    const contactInfo = {
        phone: "+91 9982069996",
        email: "enquiry@jacknjillschool.org",
        address: "Sector D, Kirti Nagar, Bhadwasiya Rd, Mata Ka Than, Jodhpur, Rajasthan 342007"
    };

    const [galleryImages, setGalleryImages] = useState([]);

    // 🔒 Guard to ensure API is called ONLY ONCE
    const hasFetched = useRef(false);

    // Root domain (DB already has /uploads/...)
    const BASE_IMAGE_URL = "https://jacknjillschool.org";

    /* -------------------- Fetch Gallery (ONE TIME ONLY) -------------------- */
    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;

        const fetchGalleryImages = async () => {
            try {
                const API_URL = "https://jacknjillschool.org/api/gallery";
                const response = await fetch(API_URL);
                const data = await response.json();

                if (Array.isArray(data)) {
                    const shuffled = [...data].sort(() => 0.5 - Math.random());
                    setGalleryImages(shuffled.slice(0, 6)); // ✅ ONLY 6 IMAGES
                }
            } catch (error) {
                console.error("Error fetching gallery images:", error);
            }
        };

        fetchGalleryImages();
    }, []);

    /* -------------------- Image URL Resolver -------------------- */
    const getImageUrl = (imgPath) => {
        if (!imgPath) return "/placeholder.jpg";

        if (imgPath.startsWith("http")) {
            return imgPath;
        }

        // DB value example: /uploads/gallery/xyz.webp
        return `${BASE_IMAGE_URL}${imgPath}`;
    };

    return (
        <footer
            className="relative bg-gray-900 text-gray-300 bg-cover bg-center bg-no-repeat bg-fixed"
            style={{ backgroundImage: `url(${footerBg})` }}
        >
            <div className="absolute inset-0 bg-gray-900 bg-opacity-80"></div>

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">

                    {/* Column 1: Logo & About */}
                    <div>
                        <img
                            className="h-24 mb-6 object-contain drop-shadow-[0_0_2px_rgba(255,255,255,0.5)]"
                            src={footerLogo}
                            alt="JNJ School Footer Logo"
                        />

                        <p className="text-gray-400 text-sm leading-relaxed mb-6">
                            A distinguished institution committed to redefining education with strong cultural values and modern pedagogy.
                        </p>

                        <div className="flex space-x-3">
                            <SocialLink href="https://www.facebook.com/profile.php?id=100064232791284" icon={<i className="fab fa-facebook-f"></i>} />
                            <SocialLink href="https://www.youtube.com/@jacknjillschool.1" icon={<i className="fab fa-youtube"></i>} />
                            <SocialLink href="https://www.instagram.com/jacknjillschool/?hl=en" icon={<i className="fab fa-instagram"></i>} />
                            <SocialLink href="https://www.linkedin.com/company/jack-n-jill-sr-sec-school/" icon={<i className="fab fa-linkedin-in"></i>} />
                        </div>
                    </div>

                    {/* Column 2: Contact Us */}
                    <div>
                        <h3 className="text-white text-2xl font-bold">Contact Us</h3>
                        <div className="w-16 h-1 bg-orange-500 mt-2 mb-8"></div>

                        <ContactInfoItem icon={<i className="fas fa-phone-alt"></i>} title="Phone" text={contactInfo.phone} />
                        <ContactInfoItem icon={<i className="fas fa-envelope"></i>} title="Email" text={contactInfo.email} />
                        <ContactInfoItem icon={<i className="fas fa-map-marker-alt"></i>} title="Address" text={contactInfo.address} />
                    </div>

                    {/* Column 3: Quick Links */}
                    <div>
                        <h3 className="text-white text-2xl font-bold">Quick Links</h3>
                        <div className="w-16 h-1 bg-orange-500 mt-2 mb-8"></div>

                        <ul className="space-y-3 text-gray-400">
                            <li><Link to="/life-at-jnj" className="hover:text-orange-500 hover:pl-2 transition-all duration-300">Admission Criteria</Link></li>
                            <li><Link to="/board-results" className="hover:text-orange-500 hover:pl-2 transition-all duration-300">Board Results</Link></li>
                            <li><Link to="/curricular" className="hover:text-orange-500 hover:pl-2 transition-all duration-300">Academic Curriculum</Link></li>
                            <li><Link to="/alumni" className="hover:text-orange-500 hover:pl-2 transition-all duration-300">Alumni Network</Link></li>
                            <li><Link to="/join-us" className="hover:text-orange-500 hover:pl-2 transition-all duration-300">Career / Join Us</Link></li>
                            <li><Link to="/contact-us" className="hover:text-orange-500 hover:pl-2 transition-all duration-300">Contact Us</Link></li>
                        </ul>
                    </div>

                    {/* Column 4: Photo Gallery */}
                    <div>
                        <h3 className="text-white text-2xl font-bold">Photo Gallery</h3>
                        <div className="w-16 h-1 bg-orange-500 mt-2 mb-8"></div>

                        <div className="grid grid-cols-3 gap-2">
                            {galleryImages.length > 0 ? (
                                galleryImages.map((item, index) => (
                                    <Link
                                        key={item.id || index}
                                        to="/gallery"
                                        className="block overflow-hidden rounded-md relative group border border-gray-700"
                                    >
                                        <div className="absolute inset-0 bg-orange-500 opacity-0 group-hover:opacity-20 transition-opacity duration-300 z-10"></div>

                                        <img
                                            src={getImageUrl(item.image_path)}
                                            alt="School Gallery"
                                            className="w-full h-20 object-cover transition-transform duration-500 group-hover:scale-110"
                                            loading="lazy"
                                            onError={(e) => {
                                                e.currentTarget.onerror = null;
                                                e.currentTarget.src = "/placeholder.jpg";
                                            }}
                                        />
                                    </Link>
                                ))
                            ) : (
                                <p className="text-gray-500 text-sm col-span-3">Loading Gallery...</p>
                            )}
                        </div>
                    </div>

                </div>
            </div>

            {/* Bottom Bar */}
            <div className="relative z-10 bg-black py-6 border-t border-gray-800">
                <p className="text-center text-gray-500 text-sm">
                    Copyright © {new Date().getFullYear()} Jack N Jill School. All Rights Reserved.
                </p>
            </div>
        </footer>
    );
};

export default Footer;